import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isUndefined } from 'lodash'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import { fetchGappReport, fetchInstallationStatement, fetchOneInstallationDocument, uploadInstallationDocument } from '../api/files.api'
import { signalDownload } from '../functions/files'
import { InstallationStatement } from '../models/file.model'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { handleReactNativeFileDownload } from '@e3dc-react/shell/Libs/webview'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { FileWithLatestStatus } from 'src/Libs/installation-statement/models'
import { getFiles } from 'src/Libs/installation-statement/api'
import { modifyFilesToGetLatestStatus } from 'src/Libs/installation-statement/hooks/use-files'
import { useIsWebviewMode } from 'src/Hooks'

export interface UseInstallationFilesHook {
  getInstallationFiles: (installationId?: number, storageDeviceLinkId?: number, hasValidStatement?: boolean) => Promise<FileWithLatestStatus[]>
  getInstallationStatement: (deviceLinkId: number, installationId: number) => Promise<InstallationStatement>
  downloadFile: (installationId: number, file: FileWithLatestStatus) => Promise<void>
  downloadGappReport: (storageId: string | undefined, language: AppLang, downloadedFileName: string) => Promise<void>
  previewFile: (installationId: number, file: FileWithLatestStatus) => Promise<string | undefined>
  uploadInstallationFile: (installationId?: number, fileObject?: FormData) => Promise<void>
  openFileInNewTab: (installationId: number | undefined, fileId: number, mimeType: string) => Promise<void>
}

const useInstallationFiles = (): UseInstallationFilesHook => {
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()
  const isWebview = useIsWebviewMode()

  const getInstallationFiles = async (installationId?: number, storageDeviceLinkId?: number): Promise<FileWithLatestStatus[]> => {
    if (isUndefined(installationId) || isUndefined(storageDeviceLinkId)) {
      return []
    }

    const results = await getFiles({
      installationId,
      deviceLinkId: storageDeviceLinkId,
      authFetch,
    })

    return modifyFilesToGetLatestStatus(results.value || [])
  }

  const getInstallationStatement = async (deviceLinkId: number, installationId: number): Promise<InstallationStatement> => {
    const response = await fetchInstallationStatement(deviceLinkId, installationId, authFetch)
    return response.value as InstallationStatement
  }

  const downloadFile = async (installationId: number, file: FileWithLatestStatus): Promise<void> => {
    const response = await fetchOneInstallationDocument(installationId, file.id, authFetch, isWebview)
    if (response.hasErrors) {
      console.warn('downloading file did not work')
      enqueueErrorStack(t('system-details.installation-statements.file-not-found'))
    } else {
      if (isWebview) {
        if (file.name && file.extension && file.mimeType) {
          handleReactNativeFileDownload(response.value as unknown as ArrayBuffer, file.name, file.extension, file.mimeType)
        }
      } else {
        const url = globalThis.URL.createObjectURL(new Blob([response.value as string]))
        signalDownload(url, `${file.name}.${file.extension}`)
      }
    }
  }

  const downloadGappReport = async (storageId: string | undefined, language: AppLang, downloadedFileName: string): Promise<void> => {
    const response = await fetchGappReport(storageId, language, authFetch, isWebview)
    const fileName = `${downloadedFileName}-${storageId}`
    const fileExtension = 'pdf'
    const fileType = 'application/pdf'

    if (response.hasErrors) {
      const error = response.error as AuthFetchError

      let errorData = error.response?.data
      /**
       * Gapp route returns a blob, so in order to get error details, we need to parse it first
       */
      if (errorData instanceof Blob) {
        try {
          const text = await errorData.text()
          errorData = JSON.parse(text)
        } catch {
          console.warn('Failed to parse error data as JSON')
        }
      }

      if (errorData?.errno === FetchErrorCodes.GappReportNotFound) {
        enqueueErrorStack(t('dashboard.documents.gapp-reports.error.not-found'))
        return
      } else if (errorData?.errno === FetchErrorCodes.GappReportFetchFailed) {
        enqueueErrorStack(t('dashboard.documents.gapp-reports.error.fetch-failed'))
        return
      } else {
        console.warn('downloading gapp report did not work')
        enqueueErrorStack(t('dashboard.documents.gapp-reports.error.download'))
      }
    } else {
      if (isWebview) {
        handleReactNativeFileDownload(response.value as unknown as ArrayBuffer, fileName, fileExtension, fileType)
      } else {
        const url = globalThis.URL.createObjectURL(new Blob([response.value as string]))
        signalDownload(url, `${fileName}.${fileExtension}`)
      }
    }
  }

  const previewFile = async (
    installationId: number | undefined,
    file: FileWithLatestStatus,
    options?: BlobPropertyBag
  ): Promise<string | undefined> => {
    try {
      const response = await fetchOneInstallationDocument(installationId, file.id, authFetch, isWebview)
      if (response.hasErrors || response.value === null) {
        console.error('Error previewing file:', response.error)
        return undefined
      }

      return globalThis.URL.createObjectURL(new Blob([response.value], options))
    } catch (error) {
      console.error('Error previewing file:', error)
      return undefined
    }
  }

  const openFileInNewTab = async (installationId: number | undefined, fileId: number, mimeType: string): Promise<void> => {
    try {
      const url = await previewFile(
        installationId,
        {
          id: fileId,
        } as FileWithLatestStatus,
        {
          type: mimeType,
        }
      )

      if (!url) {
        enqueueErrorStack(t('system-details.installation-statements.file-not-found'))
        return
      }

      const pdfWindow = window.open(url)

      /** null is returned if the browser fails to open the new browsing context, for example because it was blocked by a browser popup blocker. */
      if (pdfWindow === null) {
        enqueueErrorStack(t('notifications.blocked-by-browser'))
      }
    } catch (err) {
      console.error(err)
      enqueueErrorStack(t('system-details.installation-statements.file-not-found'))
    }
  }

  const uploadInstallationFile = async (installationId?: number, fileObject?: FormData): Promise<void> => {
    if (isUndefined(installationId) || isUndefined(fileObject)) return
    return uploadInstallationDocument(installationId, fileObject, authFetch)
  }

  return { getInstallationFiles, getInstallationStatement, uploadInstallationFile, previewFile, openFileInNewTab, downloadFile, downloadGappReport }
}

export default useInstallationFiles
